/**
 * Qué enlaces del menú ve ESTA persona, y el mismo menú contado en texto para
 * Sofi.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ POR QUÉ SOFI RECIBE EL MENÚ DESDE LA PANTALLA (2026-10-07)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Sofi contestaba «¿dónde está…?» con un manual escrito a mano en agosto, y
 * en dos meses la aplicación creció más que en todo lo anterior: sucursales,
 * almacenes, traslados, calendario, catálogo web, nómina… El manual no sabía
 * nada de eso, y alguna entrada ya decía cosas falsas («todavía no hay cambio
 * masivo de precios»).
 *
 * Un manual de rutas escrito a mano SIEMPRE se queda atrás. Así que la ruta ya
 * no se escribe: la pantalla le manda a Sofi el menú tal como lo dibuja para
 * esta persona —con sus permisos, su plan, sus ajustes y su modo—, que es la
 * misma cuenta que hace la barra de la izquierda. Una pantalla nueva en el menú
 * es una pantalla que Sofi ya conoce, sin tocar nada más.
 *
 * Lo que sigue viviendo en el manual del servidor (`sofi/manual.py`) es el
 * CÓMO y la TRAMPA de cada cosa, que no salen del menú.
 */

import { useQuery } from "@tanstack/react-query";
import { negocio } from "@/lib/endpoints";
import { useAuth } from "@/stores/auth";
import {
  GRUPO,
  GRUPOS,
  INICIO,
  SALON,
  TODAS_LAS_SUCURSALES,
  VENDER,
  type Enlace,
  type Grupo,
} from "./navegacion";

/**
 * La regla de la barra de la izquierda: si un enlace se enseña o no.
 *
 * Vivía dentro de `AppLayout`; se sacó aquí para que Sofi cuente exactamente
 * el mismo menú que se ve.
 */
export function useEnlacePermitido(): (e: Enlace) => boolean {
  const { puede } = useAuth();
  const funciones = useAuth((s) => s.plan?.funciones);
  // Las dos con la clave que ya usan la barra y Ajustes: salen de la caché.
  const { data: datosNegocio } = useQuery({
    queryKey: ["negocio"],
    queryFn: negocio.obtener,
    staleTime: 5 * 60_000,
  });
  const { data: locales } = useQuery({
    queryKey: ["sucursales"],
    queryFn: negocio.sucursales,
    staleTime: 5 * 60_000,
  });

  return (e: Enlace) => {
    if (e.permiso && !puede(e.permiso)) return false;
    if (e.permisoAlguno && !e.permisoAlguno.some((p) => puede(p))) return false;
    if (e.permisos && !e.permisos.every((p) => puede(p))) return false;
    if (e.soloSiAjuste) {
      const guardado = datosNegocio?.settings?.[e.soloSiAjuste];
      const encendido =
        guardado ??
        (e.encendidoEnModo != null &&
          (datosNegocio?.settings?.modo_operacion ?? "general") === e.encendidoEnModo);
      if (!encendido) return false;
    }
    if (e.soloSiModo && (datosNegocio?.settings?.modo_operacion ?? "general") !== e.soloSiModo)
      return false;
    if (e.noEnModo && (datosNegocio?.settings?.modo_operacion ?? "general") === e.noEnModo)
      return false;
    // ⚠️ `> 1` sobre una lista que puede no haber cargado: mientras carga,
    // `locales` es `undefined` y el enlace no se muestra. Es lo correcto aquí y
    // al revés que en `funciones` de abajo — comparar sucursales cuando solo
    // hay una es una pantalla vacía, y una pantalla vacía al entrar se lee
    // peor que un enlace que aparece medio segundo después.
    if (e.soloSiVariasSucursales && (locales?.length ?? 0) <= 1) return false;
    // `!== false` y no `=== true`: mientras la sesión carga, `funciones` es
    // `undefined` y el enlace se muestra. Un menú que va apareciendo por partes
    // al entrar se lee como que la aplicación está rota.
    if (e.soloSiFuncion && funciones?.[e.soloSiFuncion] === false) return false;
    return true;
  };
}

/** Lo que hace falta para contar el menú: ver `lineasDelMenu`. */
export type ComoSeVe = {
  permitido: (e: Enlace) => boolean;
  /** Las funciones del plan: lo que falte sale «con candado». */
  funciones?: Partial<Record<string, boolean>>;
};

/**
 * Un menú contado en líneas: «Negocio › Catálogo › Precios de venta».
 *
 * Lo que tiene candado (Premium sin contratar) se cuenta igual —el enlace se
 * ve—, pero dicho: así Sofi no manda a alguien a una pantalla que le va a
 * pedir pagar sin avisarle.
 */
export function lineasDelMenu(grupos: Grupo[], { permitido, funciones }: ComoSeVe): string[] {
  const lineas: string[] = [];
  const conCandado = (e: Enlace) =>
    e.premium && funciones?.[e.premium] === false ? " (con candado: es del plan Premium)" : "";
  for (const g of grupos) {
    for (const e of g.enlaces) {
      if (!permitido(e)) continue;
      const ruta = `${g.titulo} › ${e.etiqueta}`;
      lineas.push(ruta + conCandado(e));
      for (const h of e.hijos ?? []) {
        if (permitido(h)) lineas.push(`${ruta} › ${h.etiqueta}${conCandado(h)}`);
      }
    }
  }
  return lineas;
}

/**
 * Las pantallas de Ajustes: viven DENTRO de la pantalla, no en la barra.
 * Mismas condiciones que `AjustesPage`.
 */
function lineasDeAjustes(administra: boolean, modo: string): string[] {
  const base = "Administrar › Ajustes";
  return [
    ...(administra
      ? [
          `${base} › Datos de tu negocio`,
          `${base} › Modo de operación`,
          `${base} › Funciones`,
          ...(modo === "restaurante" ? [`${base} › Mesas y cocina`] : []),
          ...(modo === "consultorio" ? [`${base} › Consultorio`] : []),
        ]
      : []),
    `${base} › Balanza (de este aparato)`,
    `${base} › Impresora (de este aparato)`,
    ...(administra ? [`${base} › Ajustes de Sofi`, `${base} › Verificación de pagos`] : []),
    `${base} › Tu cuenta`,
    `${base} › Sugerencias`,
    ...(administra ? [`${base} › Tu plan de Softfi`] : []),
  ];
}

/**
 * El menú entero de esta persona, en texto, para mandárselo a Sofi.
 *
 * Por partes: la barra de siempre, lo que hay dentro de Ajustes y, si los
 * tiene, «Todas las sucursales» y «Todos mis negocios», que son barras
 * aparte a las que se entra desde el selector de arriba.
 */
export function useMenuParaSofi(): string[] {
  const permitido = useEnlacePermitido();
  const { puede, role, tenants } = useAuth();
  const funciones = useAuth((s) => s.plan?.funciones);
  const { data: datosNegocio } = useQuery({
    queryKey: ["negocio"],
    queryFn: negocio.obtener,
    staleTime: 5 * 60_000,
  });
  const { data: locales } = useQuery({
    queryKey: ["sucursales"],
    queryFn: negocio.sucursales,
    staleTime: 5 * 60_000,
  });
  const modo = datosNegocio?.settings?.modo_operacion ?? "general";
  const como: ComoSeVe = { permitido, funciones };

  const principal = modo === "restaurante" ? SALON : VENDER;
  const menu = [
    "## La barra de la izquierda",
    ...(permitido(principal) ? [`${principal.etiqueta} (el botón grande de arriba)`] : []),
    ...(permitido(INICIO) ? [INICIO.etiqueta] : []),
    ...lineasDelMenu(GRUPOS, como),
    "",
    "## Dentro de Administrar › Ajustes",
    ...lineasDeAjustes(puede("tenant.manage"), modo),
  ];

  const sucursales = (locales ?? []).filter((l) => l.tipo !== "almacen");
  if (sucursales.length > 1) {
    const todas = lineasDelMenu(TODAS_LAS_SUCURSALES, como);
    if (todas.length) {
      menu.push(
        "",
        "## «Todas las sucursales»: se elige en el selector de arriba a la izquierda (donde sale el nombre del negocio)",
        ...todas,
      );
    }
  }
  if (role === "owner" && tenants.length > 1) {
    menu.push(
      "",
      "## «Todos mis negocios»: se elige «Todos» en el selector de arriba a la izquierda",
      ...lineasDelMenu(GRUPO, como),
    );
  }
  return menu;
}
